// Skeleton: the shape of content that is on its way. A slow light crosses it when motion is
// allowed; under reduced motion it is a still block. Everything smaller than a whole page waits
// on these; a whole page waits on LogoSpinner. docs/operations/design-standard.md, "Feedback".

import React from 'react';

/**
 * One block, sized by `className` to what it stands in for. A `rounded-*` there replaces the
 * default corner rather than competing with it: corner utilities are emitted in a fixed order,
 * not the order written, and `rounded-button` loses to `rounded-control` in it.
 */
export const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div aria-hidden="true" className={`relative overflow-hidden bg-surface-container-high ${/\brounded-/.test(className) ? '' : 'rounded-control'} ${className}`}>
    <div className="absolute inset-0 hidden bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.05),transparent)] motion-safe:block motion-safe:animate-shimmer" />
  </div>
);

/** Lines of text on their way: full lines, the last one shorter, as a paragraph ends. */
export const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({ lines = 3, className = '' }) => (
  <div aria-hidden="true" className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }, (_, i) => (
      <Skeleton key={i} className={`h-3.5 ${lines > 1 && i === lines - 1 ? 'w-2/3' : 'w-full'}`} />
    ))}
  </div>
);

/**
 * The status around a set of skeletons: blocks say nothing to a screen reader, so this says
 * what is loading. The region they stand in for sets `aria-busy` itself.
 */
export const SkeletonGroup: React.FC<{ label: string; className?: string; children: React.ReactNode }> = ({ label, className = '', children }) => (
  <div role="status" className={className}>
    <span className="sr-only">{label}</span>
    {children}
  </div>
);

export default Skeleton;
